import { PlanType } from "@/types/auth"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ChatMessage } from "@/lib/assistant/types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Strips common markdown artifacts for plain-text display in chat bubbles.
export function sanitizeText(text: string): string {
  if (!text) return ""
  return text.replace(/<has_function_call>/g, "")
}

export function getTextFromMessage(message: ChatMessage): string {
  return message.parts
    .filter((part): part is Extract<typeof part, { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("")
}


export const getPlanTypeLabel = (planType: PlanType): string => {
  switch (planType) {
    case PlanType.STARTER_MONTHLY:
    case PlanType.STARTER_YEARLY:
      return "Starter"
    case PlanType.PRO_MONTHLY:
    case PlanType.PRO_YEARLY:
      return "Pro"
    case PlanType.BUSINESS_MONTHLY:
    case PlanType.BUSINESS_YEARLY:
      return "Business"
    case PlanType.FREE:
    default:
      return "Free"
  }
}

/* One-time starter-credit signup grant. Mirrors CREDITS.INITIAL_FREE_PLAN_CREDITS
 * in the backend, which is the number actually granted — keep the two in step. */
export const INITIAL_FREE_PLAN_CREDITS = 20;

// Credits allocated for paid subscription users (monthly renewal)
export const MONTHLY_STARTER_PLAN_CREDITS = 500;

export const MONTHLY_PRO_PLAN_CREDITS = 1000;

export const MONTHLY_BUSINESS_PLAN_CREDITS = 2500;


export const getCreditsTotal = (planType: PlanType) => {
  switch (planType) {
    case PlanType.STARTER_MONTHLY:
    case PlanType.STARTER_YEARLY:
      return MONTHLY_STARTER_PLAN_CREDITS;
    case PlanType.PRO_MONTHLY:
    case PlanType.PRO_YEARLY:
      return MONTHLY_PRO_PLAN_CREDITS;
    case PlanType.BUSINESS_MONTHLY:
    case PlanType.BUSINESS_YEARLY:
      return MONTHLY_BUSINESS_PLAN_CREDITS;
    default:
      return INITIAL_FREE_PLAN_CREDITS;
  }
};

/* Topup pricing configuration */
export const TOPUP_CONFIG = {
  PRICE_PER_CREDIT: 0.10,
  MIN_USD_AMOUNT: 5,
  MAX_USD_AMOUNT: 1000,
  MIN_CREDITS: 50,
  MAX_CREDITS: 10000,
} as const;

export const TOPUP_SUGGESTED_AMOUNTS = [5, 10, 20, 50, 100, 250];

/* Calculate credits from USD amount */
export const creditsFromUSD = (usd: number): number => {
  return Math.floor(usd / TOPUP_CONFIG.PRICE_PER_CREDIT);
};

/* Calculate USD from credits */
export const usdFromCredits = (credits: number): number => {
  return credits * TOPUP_CONFIG.PRICE_PER_CREDIT;
};